import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useProject } from '../contexts/ProjectContext';
import { dashboardService } from '../services/dashboard';

const ROLE_LABELS = {
  routed: 'Routed',
  original: 'Original',
  higher: 'Higher',
  lower: 'Lower',
};

function apiError(err) {
  return err?.response?.data?.detail || err?.detail || 'Eval failed';
}

export default function EvalsNewPage() {
  const { selectedHash, selectedProject, projects } = useProject();
  const [searchParams] = useSearchParams();
  const usageEventId = searchParams.get('usage_event');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const startedRef = useRef(false);

  useEffect(() => {
    startedRef.current = false;
  }, [selectedHash, usageEventId]);

  useEffect(() => {
    if (!selectedHash || !usageEventId) {
      setLoading(false);
      return;
    }
    if (!projects.length) return;

    if (!selectedProject?.eval_enabled) {
      setLoading(false);
      setError('Enable evals in project settings first.');
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    setLoading(true);
    setError('');
    setResults(null);

    const finish = (runs) => {
      if (cancelled) return;
      setResults(runs);
      setLoading(false);
    };

    const fail = (err) => {
      if (cancelled) return;
      setError(apiError(err));
      setLoading(false);
    };

    const runSync = () => {
      dashboardService.runEval(
        selectedHash,
        { usage_event_id: usageEventId },
        (res) => finish(res.data?.results || []),
        fail,
      );
    };

    dashboardService.evalsRuns(
      selectedHash,
      { usage_event: usageEventId, source: 'playground', page_size: 20 },
      (res) => {
        const existing = res.data?.results || [];
        if (existing.length) {
          finish(existing);
          return;
        }
        runSync();
      },
      runSync,
    );

    return () => {
      cancelled = true;
    };
  }, [selectedHash, usageEventId, selectedProject, projects]);

  return (
    <DashboardLayout title="Running eval">
      {loading && (
        <div className="flex items-center gap-3 text-sm opacity-80">
          <span className="inline-block w-4 h-4 rounded-full border-2 border-theme-primary border-t-transparent animate-spin" />
          Scoring models against your prompt…
        </div>
      )}
      {error && !loading && <p className="text-red-500 text-sm">{error}</p>}
      {results && !loading && (
        <>
          <p className="text-sm opacity-70 mb-4">Comparison for usage event {usageEventId}</p>
          <div className="overflow-x-auto rounded-xl border border-theme-secondary mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-theme-secondary bg-theme-secondary/30 text-left">
                  <th className="px-4 py-3">Model</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Latency</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((run) => (
                  <tr key={run.id} className="border-b border-theme-secondary/50">
                    <td className="px-4 py-3 font-mono text-xs">{run.model_tested}</td>
                    <td className="px-4 py-3">{ROLE_LABELS[run.model_role] || run.model_role}</td>
                    <td className="px-4 py-3">{run.score != null ? `${run.score}%` : '—'}</td>
                    <td className="px-4 py-3">{run.latency_ms != null ? `${run.latency_ms}ms` : '—'}</td>
                    <td className="px-4 py-3 capitalize">{run.status?.replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={() => navigate('/evals/')}
            className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm"
          >
            Back to Evals
          </button>
        </>
      )}
    </DashboardLayout>
  );
}
